import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    Heading,
    SimpleGrid,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SignaturePad from 'react-signature-canvas';
import { getPhysicianByToken, submitPhysicianSignature } from '../../../api';
import Logo from '../../../template/HomePage/components/Logo';

const PhysicianSignPage = () => {
    const { token } = useParams();
    const [physician, setPhysician] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const toast = useToast();
    const sigPadRef = useRef();
    const confirmRef = useRef();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const loadPhysician = async () => {
            const res = await getPhysicianByToken(token);
            if (res.success) setPhysician(res.data);
            else {
                toast({
                    title: 'Invalid or expired link',
                    description: res.error,
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            }
            setLoading(false);
        };
        loadPhysician();
    }, [token, toast]);

    const handleSubmit = async () => {
        if (sigPadRef.current.isEmpty()) {
            toast({
                title: 'Signature Required',
                description: 'Please sign before submitting.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        setIsConfirmOpen(true);
    };

    const getUserAgentAndIP = async () => {
        const userAgent = navigator.userAgent;

        // IP fetch with timeout
        const fetchIP = new Promise(async (resolve) => {
            try {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 3500);

                const res = await fetch('https://api.ipify.org?format=json', {
                    signal: controller.signal
                });
                clearTimeout(timeout);

                const data = await res.json();
                resolve({ ip: data.ip, agent: userAgent });
            } catch (err) {
                resolve({ userAgent }); // fallback without IP
            }
        });

        return fetchIP;
    };



    const confirmSubmit = async () => {
        setSubmitting(true);
        const userMetadata = await getUserAgentAndIP()
        const image = sigPadRef.current.getTrimmedCanvas().toDataURL('image/png');
        const res = await submitPhysicianSignature(token, image, userMetadata.ip || null, userMetadata.agent || null);
        setSubmitting(false);

        if (res.success) setSubmitted(true);
        else {
            toast({
                title: 'Error',
                description: res.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
        setIsConfirmOpen(false);
    };
    if (loading) return <Text p={8}>Loading...</Text>;

    if (!physician) {
        return (
            <>
                <Logo height={44} />
                <Box textAlign="center" py={10} px={6}>
                    <h1 style={{ fontSize: 30, fontWeight: 'light' }}>Broken Link</h1>
                    <Box mt={6}>
                        <p>The link you followed is not valid anymore or physician not found.</p>
                        <p>If you believe this is a mistake, contact us at <b>(888) 882-3330</b> or <b>CS@MYDLSLAB.COM</b></p>
                    </Box>
                </Box>
            </>
        );
    }

    if (submitted) {
        return (
            <>
                <Logo height={44} />
                <Box textAlign="center" py={20} px={6}>
                    <Heading color="green.500" mb={4}>
                        Thank You!
                    </Heading>
                    <Text fontSize="lg" mb={2}>
                        Your physician information has been successfully signed and submitted.
                    </Text>
                    <Text fontSize="md" color="gray.600">
                        You can now safely close this page.
                    </Text>
                </Box>
            </>
        );
    }

    return (
        <>
            <Logo height={44} />

            <Box maxW="700px" mx="auto" p={8} borderWidth="1px" borderRadius="lg" boxShadow="md" bg="white">
                <Heading mb={4} fontSize="2xl" textAlign="center" color="blue.700">
                    Physician Signature Confirmation
                </Heading>

                <Text fontSize="sm" color="gray.600" mb={6} textAlign="center">
                    Please review the information below and sign to confirm its accuracy.
                </Text>

                <Box mb={6} borderWidth="1px" borderRadius="md" p={5} bg="gray.50">
                    <SimpleGrid columns={[1, 2]} spacing={4}>
                        <Text fontWeight="bold" color="gray.600">Full Name:</Text>
                        <Text>{physician.fullName}</Text>

                        <Text fontWeight="bold" color="gray.600">NPI Number:</Text>
                        <Text>{physician.npiNumber}</Text>

                        <Text fontWeight="bold" color="gray.600">License Number:</Text>
                        <Text>{physician.license?.number || 'N/A'}</Text>

                        <Text fontWeight="bold" color="gray.600">Issuing State:</Text>
                        <Text>{physician.license?.issuingState || 'N/A'}</Text>

                        <Text fontWeight="bold" color="gray.600">License Expiration:</Text>
                        <Text>{physician.license?.expirationDate ? new Date(physician.license.expirationDate).toLocaleDateString() : 'N/A'}</Text>

                        <Text fontWeight="bold" color="gray.600">Email:</Text>
                        <Text>{physician.contact?.email || 'N/A'}</Text>

                        <Text fontWeight="bold" color="gray.600">Phone:</Text>
                        <Text>{physician.contact?.phone || 'N/A'}</Text>
                    </SimpleGrid>
                </Box>

                <Box bg="gray.100" p={4} borderRadius="md" mb={6}>
                    <Text fontSize="sm" color="gray.700">
                        If any information above is incorrect, please contact us at <strong>support@mydlslab.com</strong> or call <strong>(888) 882-3330</strong> before signing.
                    </Text>
                </Box>

                <Box>
                    <Text mb={2} fontWeight="medium">Your Digital Signature:</Text>
                    <Box mt={6} mb={6} fontSize="sm" color="gray.600">
                        <Text>
                            By signing below, you confirm that all the information shown is accurate and you are authorized to sign.
                        </Text>
                    </Box>

                    <Text color='orange.600' fontSize="sm">Please sign within the box borders</Text>

                    <Box
                        border="1px solid #ccc"
                        borderRadius="md"
                        bg="white"
                        w="100%"
                        maxW="100%"
                        overflow="hidden"
                        mt={2}
                    >
                        <SignaturePad
                            ref={sigPadRef}
                            canvasProps={{ height: 250, style: { width: '100%', display: 'block' } }}
                        />
                    </Box>

                    <Button mt={2} size="xs" colorScheme="pink" onClick={() => sigPadRef.current.clear()}>
                        Clear Signature
                    </Button>
                </Box>

                <Box mt={6} mb={6} fontSize="sm" color="gray.600">
                    <Heading mb={4} fontSize="2xl" textAlign="center" color="blue.700">
                        Agreement & Confirmation
                    </Heading>
                    <Text>
                        By submitting your signature below,
                        you affirm that the information presented is accurate and complete to the best of your knowledge.
                        <br /><br />
                        You hereby grant <strong>Diagnostic Laboratory Science </strong>
                        the ongoing authorization to <b>use your signature for all future laboratory orders initiated by you</b>,
                        without requiring your signature on each individual order.
                    </Text>
                    <Text mt={3}>
                        You retain the right to revoke this authorization at any time by contacting our clients service team at <strong>(888) 882-3330</strong>.
                    </Text>
                </Box>


                <Button mt={6} colorScheme="green" size="md" width="100%" onClick={handleSubmit}>
                    Submit Signature
                </Button>

                <AlertDialog
                    isOpen={isConfirmOpen}
                    leastDestructiveRef={confirmRef}
                    onClose={() => setIsConfirmOpen(false)}
                >
                    <AlertDialogOverlay>
                        <AlertDialogContent>
                            <AlertDialogHeader>Confirm Submission</AlertDialogHeader>
                            <AlertDialogBody>
                                Are you sure you want to submit your signature? You won't be able to edit it after submission.
                            </AlertDialogBody>
                            <AlertDialogFooter>
                                <Button ref={confirmRef} onClick={() => setIsConfirmOpen(false)}>Cancel</Button>
                                <Button colorScheme="green" isLoading={submitting} onClick={confirmSubmit} ml={3}>Yes, Submit</Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>
            </Box>
        </>
    );
};

export default PhysicianSignPage;
